import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarDays, Car, CheckCircle2, Clock, MapPin, Phone, User } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { supabase } from '../lib/supabase';
import { maskInternationalPhone, maskPlate, onlyDigits } from '../utils/masks';
import { normalizeLocalization } from '../lib/localization';

const marcas = ['Fiat', 'Volkswagen', 'Chevrolet', 'Ford', 'Toyota', 'Honda', 'Hyundai', 'Renault', 'Nissan', 'Jeep'];
const cores = ['Branco', 'Preto', 'Prata', 'Cinza', 'Vermelho', 'Azul', 'Verde', 'Amarelo', 'Outra'];
const anos = Array.from({ length: 2026 - 1990 + 1 }, (_, index) => 2026 - index);

export default function AgendamentoOnline() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [pageData, setPageData] = useState(null);
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({
    nome_cliente: '',
    telefone: '',
    email: '',
    marca: '',
    marca_custom: '',
    modelo: '',
    ano: '',
    cor: '',
    placa: '',
    km_atual: '',
    servico_id: '',
    problema_relato: '',
    data_agendamento: '',
    hora_agendamento: '',
  });

  const servicos = useMemo(() => pageData?.servicos || [], [pageData?.servicos]);
  const oficina = pageData?.oficina;
  const regional = normalizeLocalization(oficina || {});
  const selectedService = useMemo(() => servicos.find((item) => item.id === form.servico_id), [servicos, form.servico_id]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_public_booking_page', { p_slug: slug });
      if (error) {
        toast.error('Erro ao carregar agenda da oficina');
      }
      setPageData(data || null);
      setLoading(false);
    }
    load();
  }, [slug]);

  useEffect(() => {
    async function loadSlots() {
      if (!form.data_agendamento || !form.servico_id) {
        setSlots([]);
        return;
      }
      const { data, error } = await supabase.rpc('get_public_booking_slots', {
        p_slug: slug,
        p_data: form.data_agendamento,
        p_servico_id: form.servico_id,
      });
      if (error) {
        setSlots([]);
        toast.error('Não foi possível carregar horários');
        return;
      }
      setSlots(data || []);
    }
    loadSlots();
  }, [slug, form.data_agendamento, form.servico_id]);

  const next = () => {
    if (step === 1 && (!form.nome_cliente.trim() || form.telefone.replace(/\D/g, '').length < 7)) {
      toast.error('Informe nome e telefone válidos');
      return;
    }
    const marcaFinal = form.marca === '__other__' ? form.marca_custom.trim() : form.marca;
    if (step === 2 && (!marcaFinal || !form.modelo.trim() || !form.ano || !form.cor || form.placa.trim().length < 2)) {
      toast.error('Preencha os dados do veículo');
      return;
    }
    if (step === 3 && (!form.servico_id || !form.problema_relato.trim())) {
      toast.error('Escolha o serviço e descreva o problema');
      return;
    }
    setStep((current) => Math.min(current + 1, 4));
  };

  const submit = async () => {
    if (!form.data_agendamento || !form.hora_agendamento) {
      toast.error('Escolha data e horário');
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('create_public_online_booking', {
        payload: {
          slug,
          ...form,
          marca: form.marca === '__other__' ? form.marca_custom.trim() : form.marca,
          servico_nome: selectedService?.nome,
          placa: form.placa.replace('-', ''),
          odometro_unidade: regional.measurementSystem === 'imperial' ? 'mi' : 'km',
        },
      });
      if (error) throw error;
      setStep(5);
      toast.success('Solicitação enviada com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar solicitação de agendamento:', error);
      toast.error('Não foi possível enviar sua solicitação. Tente novamente em instantes.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0a0a0b] text-orange-400">Carregando agenda...</div>;
  }

  if (!pageData || pageData.blocked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0a0b] p-4 text-zinc-100">
        <Card className="max-w-md text-center">
          <h1 className="text-2xl font-bold">Oficina não encontrada</h1>
          <p className="mt-2 text-sm text-zinc-400">O link informado não está disponível para agendamento online.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] px-4 py-8 text-zinc-100">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 rounded-xl border border-white/[0.06] bg-[#111113] p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-orange-400">Agende seu atendimento</p>
              <h1 className="mt-1 font-display text-3xl font-bold">{oficina.nome}</h1>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-zinc-400">
                {oficina.telefone && <span className="flex items-center gap-1"><Phone size={15} /> {oficina.telefone}</span>}
                {oficina.endereco && <span className="flex items-center gap-1"><MapPin size={15} /> {oficina.endereco}</span>}
                <span className="flex items-center gap-1"><Clock size={15} /> {oficina.horario_funcionamento}</span>
              </div>
            </div>
            <div className="rounded-lg bg-orange-500/10 px-4 py-2 text-sm text-orange-300">Agendamento online</div>
          </div>
        </div>

        <Card>
          <div className="mb-6 grid gap-2 md:grid-cols-4">
            {['Cliente', 'Veículo', 'Serviço', 'Data'].map((label, index) => (
              <div key={label} className={`rounded-lg px-3 py-2 text-sm ${step > index ? 'bg-orange-500/20 text-orange-300' : 'bg-surface2 text-zinc-400'}`}>
                {index + 1}. {label}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-4">
              <h2 className="flex items-center gap-2 text-xl font-semibold"><User size={20} /> Seus dados</h2>
              <div className="grid gap-3 md:grid-cols-2">
                <div><label className="mb-1 block text-sm">Nome completo *</label><Input placeholder="Ex: Maria Silva" value={form.nome_cliente} onChange={(event) => setForm((prev) => ({ ...prev, nome_cliente: event.target.value }))} /></div>
                <div><label className="mb-1 block text-sm">Telefone *</label><Input placeholder="+1 555 123 4567" mask="internationalPhone" value={form.telefone} onChange={(event) => setForm((prev) => ({ ...prev, telefone: maskInternationalPhone(event.target.value) }))} /></div>
                <div className="md:col-span-2"><label className="mb-1 block text-sm">Email</label><Input type="email" placeholder="cliente@email.com" value={form.email} onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))} /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="flex items-center gap-2 text-xl font-semibold"><Car size={20} /> Veículo</h2>
              <div className="grid gap-3 md:grid-cols-2">
                <div><label className="mb-1 block text-sm">Marca *</label><Select value={form.marca} onChange={(event) => setForm((prev) => ({ ...prev, marca: event.target.value, marca_custom: event.target.value === '__other__' ? prev.marca_custom : '' }))} options={[{ value: '', label: 'Selecione' }, ...marcas.map((item) => ({ value: item, label: item })), { value: '__other__', label: 'Outra marca' }]} /></div>
                {form.marca === '__other__' && (
                  <div><label className="mb-1 block text-sm">Qual marca? *</label><Input placeholder="Digite a marca do veículo" value={form.marca_custom} onChange={(event) => setForm((prev) => ({ ...prev, marca_custom: event.target.value }))} /></div>
                )}
                <div><label className="mb-1 block text-sm">Modelo *</label><Input placeholder="Ex: Gol, Onix, Corolla" value={form.modelo} onChange={(event) => setForm((prev) => ({ ...prev, modelo: event.target.value }))} /></div>
                <div><label className="mb-1 block text-sm">Ano *</label><Select value={form.ano} onChange={(event) => setForm((prev) => ({ ...prev, ano: event.target.value }))} options={[{ value: '', label: 'Selecione' }, ...anos.map((item) => ({ value: item, label: item }))]} /></div>
                <div><label className="mb-1 block text-sm">Cor *</label><Select value={form.cor} onChange={(event) => setForm((prev) => ({ ...prev, cor: event.target.value }))} options={[{ value: '', label: 'Selecione' }, ...cores.map((item) => ({ value: item, label: item }))]} /></div>
                <div><label className="mb-1 block text-sm">{regional.vehicleRegistrationLabel} *</label><Input placeholder={regional.vehicleRegistrationLabel} mask="plate" value={form.placa} onChange={(event) => setForm((prev) => ({ ...prev, placa: maskPlate(event.target.value) }))} /></div>
                <div><label className="mb-1 block text-sm">Odometro atual ({regional.measurementSystem === 'imperial' ? 'mi' : 'km'})</label><Input placeholder="Ex: 45000" mask="integer" value={form.km_atual} onChange={(event) => setForm((prev) => ({ ...prev, km_atual: onlyDigits(event.target.value, 7) }))} /></div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Serviço</h2>
              <div><label className="mb-1 block text-sm">Serviço *</label><Select value={form.servico_id} onChange={(event) => setForm((prev) => ({ ...prev, servico_id: event.target.value, hora_agendamento: '' }))} options={[{ value: '', label: 'Selecione' }, ...servicos.map((item) => ({ value: item.id, label: `${item.nome} (${item.duracao_estimada} min)` }))]} /></div>
              <div><label className="mb-1 block text-sm">Descreva o problema do veículo *</label><textarea className="input-base min-h-28" placeholder="Ex: Barulho ao frear, revisao preventiva, troca de oleo..." value={form.problema_relato} onChange={(event) => setForm((prev) => ({ ...prev, problema_relato: event.target.value }))} /></div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h2 className="flex items-center gap-2 text-xl font-semibold"><CalendarDays size={20} /> Data e horário</h2>
              <div><label className="mb-1 block text-sm">Data *</label><Input type="date" min={new Date().toISOString().split('T')[0]} value={form.data_agendamento} onChange={(event) => setForm((prev) => ({ ...prev, data_agendamento: event.target.value, hora_agendamento: '' }))} /></div>
              {form.data_agendamento && (
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  {slots.length === 0 ? <p className="col-span-full rounded-lg border border-white/10 bg-surface2 px-3 py-3 text-sm text-zinc-400">Nenhum horário disponível para esta data. Tente outro dia ou fale com a oficina.</p> : slots.map((slot) => (
                    <button key={slot} type="button" onClick={() => setForm((prev) => ({ ...prev, hora_agendamento: slot }))} className={`rounded-lg border px-3 py-2 text-sm ${form.hora_agendamento === slot ? 'border-orange-500 bg-orange-500/20 text-orange-300' : 'border-white/10 bg-surface2 text-zinc-300'}`}>
                      {slot}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="py-10 text-center">
              <CheckCircle2 className="mx-auto mb-4 text-green-400" size={56} />
              <h2 className="text-2xl font-bold">Solicitação enviada com sucesso</h2>
              <p className="mt-2 text-sm text-zinc-400">A oficina recebeu sua solicitação e poderá confirmar o horário em breve.</p>
            </div>
          )}

          {step < 5 && (
            <div className="mt-6 flex justify-between border-t border-white/[0.06] pt-4">
              <Button variant="ghost" onClick={() => setStep((current) => Math.max(current - 1, 1))} disabled={step === 1 || submitting}>Voltar</Button>
              {step < 4 ? <Button onClick={next}>Próximo</Button> : <Button onClick={submit} disabled={submitting}>{submitting ? 'Enviando...' : 'Confirmar agendamento'}</Button>}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
